'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const brandOptions = [
  'General Enquiry',
  'Uno de 50',
  'ChloBo',
  'Anna Beck',
  'Tilly Sveaas',
  'Muru',
  'SVP Rings',
  'Gift Advice',
  'Other',
];

interface FormState {
  name: string;
  email: string;
  brand: string;
  message: string;
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-sans text-[0.65rem] font-semibold tracking-[0.2em] uppercase block mb-3 text-[#5FA828]">
      {children}
    </span>
  );
}

export default function ContactPage() {
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    brand: 'General Enquiry',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navigation />

      {/* Page Hero */}
      <div className="px-3 md:px-6 py-3 md:py-6 bg-[#FAFAFA]">
        <section
          className="relative flex items-end px-5 md:px-8 pb-10 md:pb-16 overflow-hidden rounded-lg mx-auto"
          style={{ background: '#1A1A1A', minHeight: '44vh', paddingTop: '40px', maxWidth: '1200px' }}
        >
          <div className="absolute inset-0 z-0">
            <img
              src="/assets/images/image-1788629448249.png"
              alt="Floral heart-shaped jewellery boxes"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.2) 100%)' }} />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto w-full">
            <span className="font-sans text-[0.65rem] font-semibold tracking-[0.2em] uppercase text-[#5FA828] block mb-3">Get in Touch</span>
            <h1
              className="font-display text-white"
              style={{ fontSize: 'clamp(2.4rem, 7vw, 6rem)', fontWeight: 300, lineHeight: 0.9 }}
            >
              Contact
              <br />
              <span style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.6)' }}>the Boutique.</span>
            </h1>
          </div>
        </section>
      </div>

      {/* Contact section */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16">
            {/* Form */}
            <div>
              <Eyebrow>Send a Message</Eyebrow>
              <h2
                className="font-display text-[#2B2B2B] mb-6 md:mb-8"
                style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 300, lineHeight: 0.95 }}
              >
                We&rsquo;d Love
                <br />
                <span style={{ fontStyle: 'italic' }}>to Hear From You.</span>
              </h2>

              {submitted ? (
                <div className="bg-[#F5F3EF] border border-[#E0DDD8] rounded-[4px] p-6 md:p-8 text-center">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#5FA828]/10 mx-auto mb-4">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#5FA828" strokeWidth="2">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </div>
                  <h3 className="font-display text-[#2B2B2B] text-2xl mb-2" style={{ fontWeight: 300 }}>Message Sent</h3>
                  <p className="font-sans text-sm text-[#6B6B6B]">Thank you for getting in touch. We'll get back to you as soon as possible.</p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name: '', email: '', brand: 'General Enquiry', message: '' }); }}
                    className="btn-secondary mt-6"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                    <div>
                      <label className="font-sans text-[0.65rem] tracking-[0.15em] uppercase text-[#9B9690] block mb-2">
                        Your Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        placeholder="Jane Smith"
                        className="w-full font-sans text-sm text-[#2B2B2B] bg-white border border-[#E0DDD8] rounded-[3px] px-4 py-3 outline-none focus:border-[#5FA828] transition-colors placeholder:text-[#C5C0B8]"
                      />
                    </div>
                    <div>
                      <label className="font-sans text-[0.65rem] tracking-[0.15em] uppercase text-[#9B9690] block mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        placeholder="jane@example.com"
                        className="w-full font-sans text-sm text-[#2B2B2B] bg-white border border-[#E0DDD8] rounded-[3px] px-4 py-3 outline-none focus:border-[#5FA828] transition-colors placeholder:text-[#C5C0B8]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="font-sans text-[0.65rem] tracking-[0.15em] uppercase text-[#9B9690] block mb-2">
                      Which Brand Are You Interested In?
                    </label>
                    <select
                      name="brand"
                      value={form.brand}
                      onChange={handleChange}
                      className="w-full font-sans text-sm text-[#2B2B2B] bg-white border border-[#E0DDD8] rounded-[3px] px-4 py-3 outline-none focus:border-[#5FA828] transition-colors appearance-none cursor-pointer"
                    >
                      {brandOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="font-sans text-[0.65rem] tracking-[0.15em] uppercase text-[#9B9690] block mb-2">
                      Your Message
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="Tell us what you're looking for, or ask us anything..."
                      className="w-full font-sans text-sm text-[#2B2B2B] bg-white border border-[#E0DDD8] rounded-[3px] px-4 py-3 outline-none focus:border-[#5FA828] transition-colors placeholder:text-[#C5C0B8] resize-none"
                    />
                  </div>

                  <button type="submit" className="btn-primary w-full">
                    Send Message
                  </button>
                </form>
              )}
            </div>

            {/* Contact info */}
            <div>
              <Eyebrow>Other Ways to Reach Us</Eyebrow>
              <h2
                className="font-display text-[#2B2B2B] mb-6 md:mb-8"
                style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 300, lineHeight: 0.95 }}
              >
                Visit, Call
                <br />
                <span style={{ fontStyle: 'italic' }}>or DM Us.</span>
              </h2>

              <div className="space-y-4 md:space-y-8">
                {/* Visit */}
                <div className="bg-white border border-[#E0DDD8] rounded-[4px] p-5 md:p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#5FA828]/10 flex items-center justify-center">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5FA828" strokeWidth="1.5">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-display text-[#2B2B2B] text-lg mb-1" style={{ fontWeight: 400 }}>Visit In-Store</h3>
                      <p className="font-sans text-sm text-[#6B6B6B]">Cowbridge High Street</p>
                      <p className="font-sans text-sm text-[#6B6B6B]">Cowbridge, South Wales</p>
                      <p className="font-sans text-sm text-[#9B9690] mt-1">Mon–Sat: 9:30am – 5:00pm</p>
                      <Link href="/visit" className="font-sans text-xs text-[#5FA828] hover:underline mt-2 inline-block">
                        Get directions →
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Phone */}
                <div className="bg-white border border-[#E0DDD8] rounded-[4px] p-5 md:p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#5FA828]/10 flex items-center justify-center">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5FA828" strokeWidth="1.5">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.96a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16.92z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-display text-[#2B2B2B] text-lg mb-1" style={{ fontWeight: 400 }}>Call Us</h3>
                      <a href="tel:+441446000000" className="font-sans text-sm text-[#2B2B2B] hover:text-[#5FA828] transition-colors">
                        [Phone number TBC]
                      </a>
                      <p className="font-sans text-xs text-[#9B9690] mt-1">During opening hours</p>
                    </div>
                  </div>
                </div>

                {/* Instagram */}
                <div className="bg-white border border-[#E0DDD8] rounded-[4px] p-5 md:p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#5FA828]/10 flex items-center justify-center">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5FA828" strokeWidth="1.5">
                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                        <circle cx="12" cy="12" r="4" />
                        <circle cx="17.5" cy="6.5" r="0.5" fill="#5FA828" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-display text-[#2B2B2B] text-lg mb-1" style={{ fontWeight: 400 }}>Instagram DM</h3>
                      <a
                        href="https://www.instagram.com/amanda13.jewellery/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-sans text-sm text-[#5FA828] hover:underline"
                      >
                        @amanda13.jewellery
                      </a>
                      <p className="font-sans text-xs text-[#9B9690] mt-1">DM us for quick enquiries</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
